import { useImmutableChannel } from '@/api/hooks'
import { cn } from '@/util/cn'
import { IChannel, IGif } from '@giphy/js-types'
import { Gif } from '@giphy/react-components'
import { ImgHTMLAttributes, useContext } from 'react'
import { LockIcon } from 'ui/src/icons/utility'
import { ModalContext } from '../modal'

const gifWidth = 144
const gifHeight = 88

export const PlaceholderImage = ({
    className,
    width = gifWidth,
    height = gifHeight,
    ...rest
}: ImgHTMLAttributes<HTMLImageElement>) => {
    return (
        <img
            width={width}
            height={height}
            alt=""
            className={cn('pointer-events-none block select-none object-cover', className)}
            {...rest}
        />
    )
}
export const newCollectionImage = 'https://media.giphy.com/media/fTytsfKCVyb6lB75jM/giphy.gif'
const noFeaturedGifImage = 'https://media.giphy.com/media/Id66GDfKacJzxSvhqV/giphy.gif'
export const floatCollectionImage = 'https://giphy.com/static/img/backgrounds/floating-shapes-transparent.gif'

export const MoreChildrenBadge = ({ count }: { count: number }) => (
    <div className="text-giphyWhite bg-giphyBlack/80 border absolute right-2 top-2 rounded-lg p-1 text-center text-xs font-bold">
        +{count} more
    </div>
)

export const ChannelContent = ({ channel }: { channel: IChannel }) => (
    <>
        <CollectionGif gif={channel.featured_gif} isPrivate={channel.is_private} />
        <CollectionLabel label={channel.short_display_name || channel.display_name} />
        <div className={cn(gifBorderClassName, 'absolute inset-0')}></div>
    </>
)

export const Breadcrumb = ({ channel, className }: { channel: IChannel; className?: string }) => {
    const { setModalType } = useContext(ModalContext)
    return (
        <span
            className={cn('hover:text-giphyBlue cursor-pointer', className)}
            onClick={() => {
                setModalType({
                    name: 'editCollections',
                    channelId: channel.id,
                    title: <Breadcrumbs channel={channel} />,
                })
            }}
        >
            {channel.display_name}
        </span>
    )
}
export const Breadcrumbs = ({ channel }: { channel: IChannel }) => {
    const { data } = useImmutableChannel(channel.id)
    const ancestors = data?.ancestors ? [...data.ancestors, channel] : [channel]
    return ancestors.map((channel, index) => {
        const isLast = index === ancestors.length - 1
        return (
            <div key={channel.id}>
                <Breadcrumb channel={channel} className={cn(isLast && 'text-giphyLightGrey pointer-events-none')} />
                {!isLast && <span className="mx-1">/</span>}
            </div>
        )
    })
}

export const CollectionGif = ({
    gif,
    isPrivate,
    className,
}: {
    className?: string
    gif?: IGif
    isPrivate?: boolean
}) => {
    return (
        <>
            {gif ? (
                <Gif
                    className={cn(className, '[&_img]:object-cover')}
                    gif={gif}
                    percentWidth="100%"
                    width={gifWidth}
                    height={gifHeight}
                    hideAttribution
                    noLink
                    borderRadius={0}
                    backgroundColor={gif.is_sticker ? undefined : 'rgba(0,0,0,0)'}
                />
            ) : (
                <PlaceholderImage src={noFeaturedGifImage} className={cn(className)} width="100%" height={gifHeight} />
            )}
            {isPrivate && (
                <div className="absolute inset-0 flex items-center justify-center">
                    {/* @ts-ignore use css instead of color prop */}
                    <LockIcon height={25} width={40} color={null} className="fill-giphyRed" />
                </div>
            )}
        </>
    )
}

export const CollectionLabel = ({ className, label }: { label: string; className?: string }) => {
    return (
        <div
            className={cn('overflow-hidden text-ellipsis whitespace-nowrap px-2.5 py-1.5 text-sm font-bold', className)}
            style={{ textShadow: '#000 1px 1px 2px' }}
        >
            {label}
        </div>
    )
}

export const Divider = () => {
    return <hr className="bg-giphyDarkGrey h-0.5 w-full" />
}

export const inputClassName = 'bg-giphyDarkGrey text-giphyWhite  box-border h-9  border-0 p-2.5'

export const cardClassName = 'relative flex cursor-pointer select-none flex-col overflow-hidden rounded-md border-solid'

export const colorsClassName = cn(
    `[&:nth-child(1n)]:bg-giphyAqua [&:nth-child(2n)]:bg-giphyRed [&:nth-child(3n)]:bg-giphyIndigo [&:nth-child(4n)]:bg-giphyPink [&:nth-child(5n)]:bg-giphyPurple`,
    `hover:[&:nth-child(1n)]:border-giphyAqua hover:[&:nth-child(2n)]:border-giphyRed hover:[&:nth-child(3n)]:border-giphyIndigo hover:[&:nth-child(4n)]:border-giphyPink hover:[&:nth-child(5n)]:border-giphyPurple`
)

export const gifBorderClassName = 'box-border border-solid border-inherit group-hover:border-4 rounded-md'
